import Sprite = Phaser.GameObjects.Sprite;
import {Ingredient, IngredientTypes} from "../model/ingredients";
import {KitchenAssetNames, KitchenScene} from "./scene";
import {CookSprite} from "./cook_sprite";
import {IGameSprite} from "./game_sprite_base";
import {Scene} from "phaser";
import path from "path";
import {mainLogger} from "./client_logger";

const logger = mainLogger.child({label: 'client.ingredient_sprite'})

const CHOPPED_SLASH_COLOR = 0xFFFFFF;
const CHOPPED_SLASH_ALPHA = 0.8;
const SPRITE_SIZE = 16;

// Chopped ingredient colors (circle + slash marks)
const CHOPPED_COLORS: Record<string, number> = {
    "chopped_tomato": 0xCC2222,
    "chopped_salad": 0x33AA33,
    "chopped_onion": 0xEECC66,
};

// Cooked ingredient colors (circle + steam marks)
const COOKED_COLORS: Record<string, number> = {
    "tomato_soup": 0xCC4422,
    "onion_soup": 0xDDAA44,
    "veggie_stew": 0x886622,
};

// Merged (multi-ingredient) colors
const MERGED_COLORS: Record<string, number> = {
    "mixed_salad": 0x44BB44,
    "veggie_mix":  0xAA8833,
};

// Burnt ingredient color (dark char circle)
const BURNT_COLOR = 0x222222;

export class IngredientSprite extends Sprite implements IGameSprite<Ingredient> {
    instance: Ingredient
    carrier: CookSprite = undefined

    constructor(scene, ingredient: Ingredient) {
        super(scene, ingredient.x, ingredient.y, ingredient.type);
        this.instance = ingredient
        scene.physics.add.existing(this)
        scene.children.add(this)
        this.setOrigin(0.5, 0.5)
        this.setScale(1.5);
        // 44px touch target at scale 1.5 = ~29px in local texture coords. Center on 16px sprite.
        const localHitSize = Math.round(44 / 1.5);
        const hitOffset = (SPRITE_SIZE - localHitSize) / 2;
        this.setInteractive(
            new Phaser.Geom.Rectangle(hitOffset, hitOffset, localHitSize, localHitSize),
            Phaser.Geom.Rectangle.Contains
        );
    }

    static preload(scene: Scene) {
        scene.load.spritesheet(KitchenAssetNames.tomato,
            path.join(__dirname, '../../assets/sprites/ingredients/tomato.png'), {frameWidth: 16})
        scene.load.spritesheet(KitchenAssetNames.salad,
            path.join(__dirname, '../../assets/sprites/ingredients/lettuce.png'), {frameWidth: 16})
        scene.load.spritesheet(KitchenAssetNames.tomato_salad,
            path.join(__dirname, '../../assets/sprites/ingredients/tomato_salad.png'), {frameWidth: 16})
    }

    static generateDynamicTextures(scene: Scene) {
        if (!scene.sys.renderer) return;
        // Raw onion: yellow-white layered circle
        if (!scene.textures.exists("onion")) {
            const g = scene.make.graphics({}, false);
            g.fillStyle(0xEEDD88, 1);
            g.fillCircle(8, 8, 7);
            g.lineStyle(1, 0xCCBB44, 0.9);
            g.strokeCircle(8, 8, 5);
            g.strokeCircle(8, 8, 3);
            g.generateTexture("onion", SPRITE_SIZE, SPRITE_SIZE);
            g.destroy();
        }
        // Chopped textures: circle with slash marks
        for (const [choppedType, color] of Object.entries(CHOPPED_COLORS)) {
            if (scene.textures.exists(choppedType)) continue;
            const g = scene.make.graphics({}, false);
            g.fillStyle(color, 1);
            g.fillCircle(8, 8, 7);
            g.lineStyle(1, CHOPPED_SLASH_COLOR, CHOPPED_SLASH_ALPHA);
            g.lineBetween(3, 2, 7, 14);
            g.lineBetween(7, 2, 11, 14);
            g.lineBetween(11, 2, 15, 14);
            g.generateTexture(choppedType, SPRITE_SIZE, SPRITE_SIZE);
            g.destroy();
        }
        // Cooked textures: circle with steam wisps
        for (const [cookedType, color] of Object.entries(COOKED_COLORS)) {
            if (scene.textures.exists(cookedType)) continue;
            const g = scene.make.graphics({}, false);
            g.fillStyle(color, 1);
            g.fillCircle(8, 10, 6);
            // Bowl shape
            g.lineStyle(1, 0xDDDDDD, 0.9);
            g.strokeCircle(8, 10, 6);
            // Steam wisps
            g.lineStyle(1, 0xFFFFFF, 0.6);
            g.lineBetween(5, 5, 4, 1);
            g.lineBetween(8, 4, 9, 1);
            g.lineBetween(11, 5, 12, 1);
            g.generateTexture(cookedType, SPRITE_SIZE, SPRITE_SIZE);
            g.destroy();
        }
        // Merged ingredient textures: split circle showing combined ingredients
        for (const [mergedType, color] of Object.entries(MERGED_COLORS)) {
            if (scene.textures.exists(mergedType)) continue;
            const g = scene.make.graphics({}, false);
            g.fillStyle(color, 1);
            g.fillCircle(8, 8, 7);
            // Dividing line to hint at multiple ingredients
            g.lineStyle(1, 0xFFFFFF, 0.6);
            g.lineBetween(8, 1, 8, 15);
            g.generateTexture(mergedType, SPRITE_SIZE, SPRITE_SIZE);
            g.destroy();
        }
        // Burnt texture: dark charred circle with X mark
        if (!scene.textures.exists(IngredientTypes.BURNT)) {
            const g = scene.make.graphics({}, false);
            g.fillStyle(BURNT_COLOR, 1);
            g.fillCircle(8, 8, 7);
            g.lineStyle(2, 0x555555, 0.9);
            g.lineBetween(4, 4, 12, 12);
            g.lineBetween(12, 4, 4, 12);
            g.generateTexture(IngredientTypes.BURNT, SPRITE_SIZE, SPRITE_SIZE);
            g.destroy();
        }
    }

    bePicked(cook: CookSprite) {
        this.carrier = cook
        this.x = 0
        this.y = 0
    }

    beDropped() {
        this.carrier = undefined
    }

    bindToInstance() {
        this.instance.listen('carrier_id', (value) => {
            if (value) {
                this.attachToCook(value)
            } else {
                this.detachFromCook()
            }
        })
        this.instance.listen('x', (value) => {
            if (this.instance.carrier_id == undefined)
                this.setX(value)
        })
        this.instance.listen('y', (value) => {
            if (this.instance.carrier_id == undefined)
                this.setY(value)
        })
        this.instance.listen('type', (value) => {
            this.setTexture(value);
        })
    }

    private attachToCook(cookId: string) {
        const kitchenScene = this.scene as KitchenScene
        const cookSprite = kitchenScene.cookObjects.findById(cookId)
        if (!cookSprite) {
            logger.warn(`Cannot attach ingredient to cook ${cookId}: cook sprite not found`)
            return
        }
        this.carrier = cookSprite
        cookSprite.carry(this)
        logger.info(`${this.instance.type}-${this.instance.id} attached to ${cookSprite.instance.id}`)
    }

    private detachFromCook() {
        if (!this.carrier) return
        const kitchenScene = this.scene as KitchenScene
        const cookSprite = this.carrier
        cookSprite.drop()
        kitchenScene.restingIngredientsLayer.add(this)
        this.setVisible(true)
        this.setPosition(this.instance.x, this.instance.y)
        logger.info(`${this.instance.type}-${this.instance.id} dropped at (${this.instance.x},${this.instance.y})`)
    }
}
